export type DebugTraceSource = "client" | "server";

export type DebugTraceCategory =
  | "auth"
  | "chat"
  | "llm"
  | "tool"
  | "entity"
  | "stream"
  | "error";

export type DebugTraceLevel = "info" | "warn" | "error";

export interface DebugTraceEvent {
  id: string;
  timestamp: string;
  source: DebugTraceSource;
  category: DebugTraceCategory;
  level: DebugTraceLevel;
  title: string;
  turnId?: string;
  messageId?: string;
  toolName?: string;
  payload?: unknown;
}

export interface DebugTraceDataPart {
  type: "data-debugEvent";
  data: DebugTraceEvent;
}

interface PartialDebugTraceEvent {
  id?: string;
  timestamp?: string;
  source?: DebugTraceSource;
  category?: DebugTraceCategory;
  level?: DebugTraceLevel;
  title?: string;
  turnId?: string;
  messageId?: string;
  toolName?: string;
  payload?: unknown;
}

const VALID_SOURCES: DebugTraceSource[] = ["client", "server"];
const VALID_CATEGORIES: DebugTraceCategory[] = [
  "auth",
  "chat",
  "llm",
  "tool",
  "entity",
  "stream",
  "error"
];
const VALID_LEVELS: DebugTraceLevel[] = ["info", "warn", "error"];

export function generateDebugTraceId(prefix = "trace"): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizeDebugTraceEvent(
  input: PartialDebugTraceEvent
): DebugTraceEvent {
  return {
    id: input.id || generateDebugTraceId(),
    timestamp: input.timestamp || new Date().toISOString(),
    source: VALID_SOURCES.includes(input.source as DebugTraceSource)
      ? (input.source as DebugTraceSource)
      : "client",
    category: VALID_CATEGORIES.includes(input.category as DebugTraceCategory)
      ? (input.category as DebugTraceCategory)
      : "chat",
    level: VALID_LEVELS.includes(input.level as DebugTraceLevel)
      ? (input.level as DebugTraceLevel)
      : "info",
    title: input.title || "Untitled event",
    ...(input.turnId ? { turnId: input.turnId } : {}),
    ...(input.messageId ? { messageId: input.messageId } : {}),
    ...(input.toolName ? { toolName: input.toolName } : {}),
    ...(input.payload !== undefined ? { payload: input.payload } : {})
  };
}

export function sortDebugTraceEvents(events: DebugTraceEvent[]): DebugTraceEvent[] {
  return [...events].sort((a, b) => {
    const timeA = Date.parse(a.timestamp);
    const timeB = Date.parse(b.timestamp);

    if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
    if (Number.isNaN(timeA)) return 1;
    if (Number.isNaN(timeB)) return -1;

    if (timeA !== timeB) {
      return timeA - timeB;
    }

    return a.id.localeCompare(b.id);
  });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isDebugTraceDataPart(part: unknown): part is DebugTraceDataPart {
  if (!isObject(part) || part.type !== "data-debugEvent" || !isObject(part.data)) {
    return false;
  }

  const data = part.data as Partial<DebugTraceEvent>;
  return (
    typeof data.id === "string" &&
    typeof data.timestamp === "string" &&
    typeof data.title === "string" &&
    VALID_SOURCES.includes(data.source as DebugTraceSource) &&
    VALID_CATEGORIES.includes(data.category as DebugTraceCategory) &&
    VALID_LEVELS.includes(data.level as DebugTraceLevel)
  );
}

function redactSensitiveKey(key: string, value: unknown): unknown {
  const normalized = key.toLowerCase();
  const isTokenUsageMetricKey =
    normalized.includes("tokens") ||
    normalized.includes("tokendetails") ||
    normalized.includes("token_details") ||
    normalized.includes("tokencount");

  if (
    normalized.includes("authorization") ||
    (normalized.includes("token") && !isTokenUsageMetricKey) ||
    normalized.includes("secret") ||
    normalized.includes("password")
  ) {
    return "[REDACTED]";
  }

  return value;
}

export function redactDebugPayload(payload: unknown): unknown {
  if (Array.isArray(payload)) {
    return payload.map((item) => redactDebugPayload(item));
  }

  if (isObject(payload)) {
    return Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [
        key,
        redactDebugPayload(redactSensitiveKey(key, value))
      ])
    );
  }

  return payload;
}
